import { useState } from "react"
import { Alert, DialogContent, DialogTitle, Stack } from "@mui/material"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { DataInputDialog } from "lib/@dialogs/data-input-dialog"
import { If, Else } from "lib/@components/switch"
import { LEGISLATION_FOLDER } from "../legislation-library-helpers"
import { isValidLegislationUrl } from "../server/lib/legislation-library-validation.mjs"
import { LegislationLibraryUrlField, stripScheme } from "./legislation-library-url-field"
import { LegislationLibraryDestinationField } from "./legislation-library-destination-field"
import { LegislationLibraryFoldersField } from "./legislation-library-folders-field"

export function LegislationLibraryEditDialog({ node, isCreate = false, onSubmit }) {
    const isFolder = node?.type === LEGISLATION_FOLDER
    const initialValue = isFolder
        ? { name: node?.name ?? "" }
        : { title: node?.title ?? "", url: node?.url ?? "", description: node?.description ?? "" }

    const [submitError, setSubmitError] = useState(null)

    const folderTitle = isCreate ? "Add Legislation Folder" : "Edit Legislation Folder"
    const legislationTitle = isCreate ? "Add Legislation" : "Edit Legislation"
    const okCaption = isCreate ? "Add" : "Save"
    const okIcon = isCreate ? "material-symbols:check-rounded" : "mdi:content-save-outline"

    return (
        // okDisabled alone gates the button: startValid skips DataInputDialog's change-gated flag, and
        // sideEffects on each field redraws the dialog so the button rechecks on every change, paste included.
        <DataInputDialog
            initialValue={initialValue}
            startValid
            okCaption={okCaption}
            okIcon={okIcon}
            okDisabled={isFolder ? checkFolderDisabled : checkLegislationDisabled}
            beforeOk={handleBeforeOk}
        >
            <DialogTitle data-cy="legislation-edit-dialog-title">
                {isFolder ? folderTitle : legislationTitle}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <If value={isFolder}>
                        <BoundTextField
                            field="name"
                            label="Name"
                            autoFocus
                            sideEffects
                            data-cy="legislation-folder-name-input"
                        />
                        <If value={!isCreate}>
                            <LegislationLibraryDestinationField id={node?.id} />
                        </If>
                        <Else>
                            <BoundTextField
                                field="title"
                                label="Name"
                                autoFocus
                                sideEffects
                                data-cy="legislation-name-input"
                            />
                            <LegislationLibraryUrlField sideEffects />
                            <BoundTextField
                                field="description"
                                label="Description (Optional)"
                                multiline
                                rows={3}
                                data-cy="legislation-description-input"
                            />
                            <If value={!isCreate}>
                                <LegislationLibraryFoldersField id={node?.id} />
                            </If>
                        </Else>
                    </If>
                    <If value={!!submitError}>
                        <Alert severity="error" data-cy="legislation-dialog-error">
                            {submitError}
                        </Alert>
                    </If>
                </Stack>
            </DialogContent>
        </DataInputDialog>
    )

    async function handleBeforeOk(value) {
        // Enter submits without blurring the URL field, so its scheme may not have been stripped yet.
        const submitted = isFolder ? value : { ...value, url: stripScheme(value.url) }
        if (!onSubmit) return submitted
        setSubmitError(null)
        try {
            return await onSubmit(submitted)
        } catch (e) {
            if (!e?.silent) setSubmitError(e?.message ?? "Something went wrong")
            throw e
        }
    }
}

function checkFolderDisabled(v) {
    return !v?.name?.trim()
}

function checkLegislationDisabled(v) {
    return !v?.title?.trim() || !isValidLegislationUrl(v?.url)
}
